import { useState, useEffect } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { Button } from "@/components/ui/button";
import { 
  Briefcase, FileText, Menu, X, Zap, User, LogOut, Shield, Send, CreditCard,
  Sun, Moon, Monitor, Heart, Settings, Bell, Lock, FileText as FileText2, MessageCircle
} from "lucide-react";
import { supabase } from "@/integrations/supabase/client";
import type { User as SupabaseUser } from "@supabase/supabase-js";
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuSeparator, DropdownMenuTrigger, DropdownMenuLabel,
} from "@/components/ui/dropdown-menu";
import { LanguageSwitcher } from "@/components/layout/LanguageSwitcher";
import { useLanguage } from "@/i18n/LanguageContext";

const TELEGRAM_BOT_URL = "https://t.me/RemoteJobsCV_bot";


type Theme = 'dark' | 'light' | 'system';

function applyTheme(theme: Theme) {
  const root = document.documentElement;
  if (theme === 'system') {
    const prefersDark = window.matchMedia('(prefers-color-scheme: dark)').matches;
    root.classList.toggle('light', !prefersDark);
  } else if (theme === 'light') {
    root.classList.add('light');
  } else {
    root.classList.remove('light');
  }
}

export function Navbar() {
  const [isOpen, setIsOpen] = useState(false);
  const [user, setUser] = useState<SupabaseUser | null>(null);
  const [isAdmin, setIsAdmin] = useState(false);
  const [theme, setTheme] = useState<Theme>('dark');
  const location = useLocation();
  const navigate = useNavigate();
  const { t } = useLanguage();

  const navLinks = [
    { href: "/jobs", label: t.nav.findJobs, icon: Briefcase },
    { href: "/cv-builder", label: t.nav.cvBuilder, icon: FileText },
    { href: "/pricing", label: t.nav.pricing, icon: CreditCard },
  ];

  // Load theme from localStorage on mount
  useEffect(() => {
    const saved = (localStorage.getItem('theme') as Theme) || 'dark';
    setTheme(saved);
    applyTheme(saved);
  }, []);

  const changeTheme = async (newTheme: Theme) => {
    setTheme(newTheme);
    applyTheme(newTheme);
    localStorage.setItem('theme', newTheme);
    // Persist to profile if logged in
    const { data: { session } } = await supabase.auth.getSession();
    if (session?.user) {
      await supabase.from('profiles').update({ theme: newTheme } as any).eq('user_id', session.user.id);
    }
  };

  const checkAdminStatus = async (userId: string) => {
    // Server-side only: check roles via RPC (owner role assigned by DB trigger on signup)
    const { data: adminCheck } = await supabase.rpc('has_role', { _user_id: userId, _role: 'admin' });
    const { data: ownerCheck } = await supabase.rpc('has_role', { _user_id: userId, _role: 'owner' as any });
    setIsAdmin(adminCheck === true || ownerCheck === true);
  };

  useEffect(() => {
    const { data: { subscription } } = supabase.auth.onAuthStateChange(
      (event, session) => {
        setUser(session?.user ?? null);
        if (session?.user) {
          setTimeout(() => { checkAdminStatus(session.user.id); }, 0);
        } else {
          setIsAdmin(false);
        }
      }
    );
    supabase.auth.getSession().then(({ data: { session } }) => {
      setUser(session?.user ?? null);
      if (session?.user) { 
        checkAdminStatus(session.user.id);
        // Load theme from profile
        supabase.from('profiles').select('theme').eq('user_id', session.user.id).single().then(({ data }) => {
          if (data?.theme) {
            const t = data.theme as Theme;
            setTheme(t);
            applyTheme(t);
          }
        });
      }
    });
    return () => subscription.unsubscribe();
  }, []);

  const handleSignOut = async () => {
    await supabase.auth.signOut();
    setUser(null);
    setIsAdmin(false);
    navigate('/');
  };

  const themeIcon = theme === 'light' ? <Sun className="w-4 h-4" /> : theme === 'system' ? <Monitor className="w-4 h-4" /> : <Moon className="w-4 h-4" />;
  const themeLabel = theme === 'light' ? 'Light' : theme === 'system' ? 'System' : 'Dark';

  return (
    <motion.header 
      initial={{ y: -100 }}
      animate={{ y: 0 }}
      className="fixed top-0 left-0 right-0 z-50 bg-glass border-b border-white/5"
    >
      <div className="container mx-auto px-4">
        <div className="flex items-center justify-between h-16">
          {/* Logo */}
          <Link to="/" className="flex items-center gap-2 group">
            <div className="relative">
              <div className="w-10 h-10 rounded-xl bg-gradient-ai flex items-center justify-center shadow-glow">
                <Zap className="w-5 h-5 text-accent-foreground" />
              </div>
              <div className="absolute -inset-1 bg-gradient-ai rounded-xl opacity-0 group-hover:opacity-30 blur transition-opacity duration-300" />
            </div>
            <span className="font-display font-bold text-xl text-foreground">
              Remote<span className="text-gradient-ai">AI</span>
            </span>
          </Link>

          {/* Desktop Navigation */}
          <nav className="hidden md:flex items-center gap-1">
            {navLinks.map((link) => {
              const Icon = link.icon;
              const isActive = location.pathname === link.href;
              return (
                <Link key={link.href} to={link.href}>
                  <Button variant={isActive ? "secondary" : "ghost"} size="sm" className={isActive ? "bg-secondary/80" : ""}>
                    <Icon className="w-4 h-4" />
                    {link.label}
                  </Button>
                </Link>
              );
            })}
          </nav>

          {/* Desktop Actions */}
          <div className="hidden md:flex items-center gap-3">
            <LanguageSwitcher />
            <a href={TELEGRAM_BOT_URL} target="_blank" rel="noopener noreferrer">
              <Button variant="ai" size="sm">
                <Send className="w-4 h-4" />
                {t.nav.openBot}
              </Button>
            </a>
            {user ? (
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <Button variant="ghost" size="sm" className="gap-2">
                    <div className="w-6 h-6 rounded-full bg-gradient-ai flex items-center justify-center">
                      <User className="w-3 h-3 text-accent-foreground" />
                    </div>
                    <span className="max-w-[120px] truncate">
                      {user.user_metadata?.full_name || user.email?.split('@')[0]}
                    </span>
                  </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end" className="w-56">
                  <DropdownMenuLabel className="text-xs text-muted-foreground font-normal truncate">
                    {user.email}
                    {isAdmin && <span className="ml-1 text-primary font-semibold">· Admin</span>}
                  </DropdownMenuLabel>
                  <DropdownMenuSeparator />
                  {isAdmin && (
                    <>
                      <DropdownMenuItem onClick={() => navigate('/admin')}>
                        <Shield className="w-4 h-4 mr-2 text-primary" />
                        Admin Dashboard
                      </DropdownMenuItem>
                      <DropdownMenuSeparator />
                    </>
                  )}
                  <DropdownMenuItem onClick={() => navigate('/pricing')}>
                    <CreditCard className="w-4 h-4 mr-2" />
                    Pricing & Plans
                  </DropdownMenuItem>
                  <DropdownMenuSeparator />
                  {/* Theme toggle */}
                  <DropdownMenuLabel className="text-xs text-muted-foreground py-1">Theme</DropdownMenuLabel>
                  <DropdownMenuItem onClick={() => changeTheme('dark')} className={theme === 'dark' ? 'bg-secondary' : ''}>
                    <Moon className="w-4 h-4 mr-2" /> Dark {theme === 'dark' && '✓'}
                  </DropdownMenuItem>
                  <DropdownMenuItem onClick={() => changeTheme('light')} className={theme === 'light' ? 'bg-secondary' : ''}>
                    <Sun className="w-4 h-4 mr-2" /> Light {theme === 'light' && '✓'}
                  </DropdownMenuItem>
                  <DropdownMenuItem onClick={() => changeTheme('system')} className={theme === 'system' ? 'bg-secondary' : ''}>
                    <Monitor className="w-4 h-4 mr-2" /> System {theme === 'system' && '✓'}
                  </DropdownMenuItem>
                  <DropdownMenuSeparator />
                  <DropdownMenuItem onClick={() => navigate('/support')}>
                    <MessageCircle className="w-4 h-4 mr-2" />
                    Support
                  </DropdownMenuItem>
                  <DropdownMenuItem asChild>
                    <a href={TELEGRAM_BOT_URL} target="_blank" rel="noopener noreferrer" className="flex items-center">
                      <Heart className="w-4 h-4 mr-2 text-destructive" />
                      Donate / Support Dev
                    </a>
                  </DropdownMenuItem>
                  <DropdownMenuSeparator />
                  <DropdownMenuItem onClick={handleSignOut} className="text-destructive focus:text-destructive">
                    <LogOut className="w-4 h-4 mr-2" />
                    {t.nav.signOut}
                  </DropdownMenuItem>
                </DropdownMenuContent>
              </DropdownMenu>
            ) : (
              <Link to="/auth">
                <Button variant="ghost" size="sm">
                  <User className="w-4 h-4" />
                  {t.nav.signIn}
                </Button>
              </Link>
            )}
          </div>

          {/* Mobile Menu Toggle */}
          <button onClick={() => setIsOpen(!isOpen)} className="md:hidden p-2 text-foreground">
            {isOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
          </button>
        </div>
      </div>

      {/* Mobile Menu */}
      <AnimatePresence>
        {isOpen && (
          <motion.div
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: "auto" }}
            exit={{ opacity: 0, height: 0 }}
            className="md:hidden bg-card border-b border-border"
          >
            <nav className="container mx-auto px-4 py-4 flex flex-col gap-2">
              {navLinks.map((link) => {
                const Icon = link.icon;
                const isActive = location.pathname === link.href;
                return (
                  <Link key={link.href} to={link.href} onClick={() => setIsOpen(false)}>
                    <Button variant={isActive ? "secondary" : "ghost"} className="w-full justify-start">
                      <Icon className="w-4 h-4" />
                      {link.label}
                    </Button>
                  </Link>
                );
              })}
              <a href={TELEGRAM_BOT_URL} target="_blank" rel="noopener noreferrer" onClick={() => setIsOpen(false)}>
                <Button variant="ai" className="w-full">
                  <Send className="w-4 h-4" />
                  {t.nav.openBot}
                </Button>
              </a>
              <div className="flex items-center justify-between pt-4 border-t border-border mt-2">
                <LanguageSwitcher />
                <div className="flex flex-col gap-2">
                  {user ? (
                    <>
                      {isAdmin && (
                        <Link to="/admin" onClick={() => setIsOpen(false)}>
                          <Button variant="ghost" size="sm">
                            <Shield className="w-4 h-4" />
                            Admin
                          </Button>
                        </Link>
                      )}
                      {/* Theme toggle row */}
                      <div className="flex gap-1">
                        <Button variant={theme === 'dark' ? 'secondary' : 'ghost'} size="sm" onClick={() => changeTheme('dark')} title="Dark">
                          <Moon className="w-4 h-4" />
                        </Button>
                        <Button variant={theme === 'light' ? 'secondary' : 'ghost'} size="sm" onClick={() => changeTheme('light')} title="Light">
                          <Sun className="w-4 h-4" />
                        </Button>
                        <Button variant={theme === 'system' ? 'secondary' : 'ghost'} size="sm" onClick={() => changeTheme('system')} title="System">
                          <Monitor className="w-4 h-4" />
                        </Button>
                      </div>
                      <Button variant="ghost" size="sm" onClick={() => { handleSignOut(); setIsOpen(false); }}>
                        <LogOut className="w-4 h-4" />
                        {t.nav.signOut}
                      </Button>
                    </>
                  ) : (
                    <Link to="/auth" onClick={() => setIsOpen(false)}>
                      <Button variant="ghost" size="sm">
                        <User className="w-4 h-4" />
                        {t.nav.signIn}
                      </Button>
                    </Link>
                  )}
                </div>
              </div>
            </nav>
          </motion.div>
        )}
      </AnimatePresence>
    </motion.header>
  );
}
